import React, { useState, useRef, useEffect } from "react";
import { Globe, ChevronDown, Check, Plus } from "lucide-react";
import type { WebsiteRecord } from "../../types/telemetry.ts";
import { WebsiteBadge } from "./WebsiteBadge.tsx";

interface WebsiteSelectorDropdownProps {
  websites: WebsiteRecord[];
  selectedWebsite: WebsiteRecord | null;
  onSelectWebsite: (website: WebsiteRecord) => void;
  onAddWebsite?: () => void;
  disabled?: boolean;
}

export const WebsiteSelectorDropdown: React.FC<WebsiteSelectorDropdownProps> = ({
  websites,
  selectedWebsite,
  onSelectWebsite,
  onAddWebsite,
  disabled = false,
}: WebsiteSelectorDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  if (!selectedWebsite && websites.length === 0) {
    return null;
  }

  const currentSite = selectedWebsite || websites[0];
  const canOpen = !disabled && (websites.length > 1 || Boolean(onAddWebsite));

  return (
    <div ref={containerRef} className="relative inline-block text-left">
      <button
        type="button"
        id="website-selector-button"
        data-testid="website-selector-trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select website"
        disabled={!canOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex items-center gap-2 px-2.5 py-1 rounded bg-panel-subtle hover:bg-panel border border-border text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand ${
          !canOpen ? "cursor-default hover:bg-panel-subtle" : "cursor-pointer"
        }`}
      >
        <Globe className="w-3.5 h-3.5 text-brand shrink-0" />
        <div className="flex items-center gap-1.5 text-xs">
          <span className="font-semibold text-foreground max-w-[140px] sm:max-w-[180px] truncate">
            {currentSite.display_name}
          </span>
          <span className="text-[11px] font-mono text-foreground-muted hidden sm:inline truncate max-w-[140px]">
            ({currentSite.public_hostname})
          </span>
        </div>
        {canOpen && (
          <ChevronDown
            className={`w-3.5 h-3.5 text-foreground-muted transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      {isOpen && (
        <div
          role="listbox"
          aria-label="Registered websites"
          data-testid="website-selector-menu"
          className="absolute left-0 mt-1.5 w-72 sm:w-80 rounded-md bg-panel border border-border shadow-xl py-1 z-50 focus:outline-none"
        >
          <div className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider text-foreground-muted border-b border-border/50">
            Registered Websites ({websites.length})
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {websites.map((site) => {
              const isSelected = site.id === currentSite.id;
              return (
                <button
                  key={site.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onSelectWebsite(site);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 flex items-center justify-between text-xs hover:bg-panel-hover transition-colors ${
                    isSelected ? "bg-panel-hover/50 text-foreground" : "text-foreground-muted hover:text-foreground"
                  }`}
                >
                  <div className="flex flex-col min-w-0 mr-2">
                    <span className="font-medium text-foreground truncate">
                      {site.display_name}
                    </span>
                    <span className="font-mono text-[11px] text-foreground-muted truncate">
                      {site.public_hostname}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <WebsiteBadge status={site.onboarding_status} />
                    {isSelected && <Check className="w-3.5 h-3.5 text-brand shrink-0" />}
                  </div>
                </button>
              );
            })}
          </div>

          {onAddWebsite && (
            <div className="border-t border-border/60 pt-1 mt-1 px-1">
              <button
                type="button"
                data-testid="add-website-dropdown-btn"
                onClick={() => {
                  setIsOpen(false);
                  onAddWebsite();
                }}
                className="w-full text-left px-2.5 py-1.5 flex items-center gap-2 text-xs font-mono text-brand hover:bg-panel-hover rounded transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand"
              >
                <Plus className="w-3.5 h-3.5 shrink-0" />
                <span>Add website</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
